/**
 * A bend that turns AWAY from the cabinets — the fronts on the outside of the
 * turn (angled-runs design 2026-11-03). Nothing collides there; a wedge opens
 * between the two rows of fronts. The user's rulings:
 *
 *   - the counter always comes to a point;
 *   - at a right angle one leg — the LONGER — runs through to the outside
 *     corner and the other butts against its side, so no filler is needed.
 *     Exactly 90° (`bendGeometry.isRightTurn`), not the wall corners' 5°: the
 *     angle is the one the user drew, and at 87° a leg run through as if
 *     square overlapped the other (the user, 2026-11-03);
 *   - at any other angle both legs run to the bend and a filler takes the
 *     wedge (bodyless under a vendor line, which makes no such box — the same
 *     butt joint `fillRun` builds at an angled wall corner).
 *
 * Only a bend through a DRAWN island leg is planned here (`drawnFreeLeg`);
 * two walls meeting at a convex room corner keep whatever the corner plan
 * gives them today.
 *
 * Pure. No I/O.
 */

const { getWallAxes } = require('../kitchen-assembly/wallMath');
const {
  isRightTurn,
  legDirection,
  legEndpoints,
  legLength,
  lineCrossing,
  turnDeg,
} = require('./bendGeometry');
const { buildPolygonFiller } = require('./polygonFiller');

const dot = (a, b) => a.x * b.x + a.z * b.z;
const add = (a, b) => ({ x: a.x + b.x, z: a.z + b.z });
const mul = (a, s) => ({ x: a.x * s, z: a.z * s });

const sub = (a, b) => ({ x: a.x - b.x, z: a.z - b.z });

/**
 * Where the two front edges of a bend away from the cabinets meet: the
 * crossing of leg A's front edge, run on past its end corner `fa`, and leg
 * B's, run back from its start corner `fb` — or null when they cross INSIDE
 * one of the legs instead. With equal depths they always meet in the open
 * wedge; with a much deeper leg at a small turn its front runs past the
 * other's crossing, and the wedge closes straight from corner to corner
 * (found 2026-11-03: a 35"-deep leg after a 24" one).
 */
function frontPoint(fa, dA, fb, dB) {
  const p = lineCrossing(fa, dA, fb, dB);
  if (!p) return null;
  const pastA = dot(sub(p, fa), dA) >= -1e-9;
  const beforeB = dot(sub(p, fb), dB) <= 1e-9;
  return pastA && beforeB ? p : null;
}

/** Does leg B turn away from leg A's cabinets (their inward normal)? */
function isOutsideBend(segA, segB) {
  const nA = segA?.wall ? getWallAxes(segA.wall)?.wallNormal : null;
  const dB = legDirection(segB);
  return Boolean(nA && dB) && dot(dB, nA) < -1e-9;
}

/**
 * How an outside bend is filled.
 *
 * `trimA` / `trimB` are corner trims in `fillRun`'s sense — metres taken off
 * leg A's END and leg B's START — where a NEGATIVE trim runs the leg past the
 * vertex: the through leg's extension over the outside corner square, as deep
 * as the other leg's row.
 *
 * @param {{ segA, segB, depthA:number, depthB:number }} a
 * @returns {{ mode:'through'|'wedge', through:'A'|'B'|null, trimA:number,
 *   trimB:number, wedge:Array<{x,z}>|null }}
 */
function planOutsideBend({ segA, segB, depthA, depthB }) {
  const dA = legDirection(segA);
  const dB = legDirection(segB);
  if (isRightTurn(turnDeg(dA, dB))) {
    const through = legLength(segA) >= legLength(segB) ? 'A' : 'B';
    return {
      mode: 'through',
      through,
      trimA: through === 'A' ? -depthB : 0,
      trimB: through === 'B' ? -depthA : 0,
      wedge: null,
    };
  }
  const V = legEndpoints(segA).end;
  const nA = getWallAxes(segA.wall).wallNormal;
  const nB = getWallAxes(segB.wall).wallNormal;
  const fa = add(V, mul(nA, depthA));
  const fb = add(V, mul(nB, depthB));
  const point = frontPoint(fa, dA, fb, dB);
  return {
    mode: 'wedge',
    through: null,
    trimA: 0,
    trimB: 0,
    wedge: point ? [V, fa, point, fb] : [V, fa, fb],
  };
}

/**
 * The filler body that takes the wedge (`polygonFiller.js`). Null for a plan
 * with no wedge.
 */
function buildOutsideWedgeBody({ plan, run, cornerIndex, floorY, height }) {
  if (!plan?.wedge) return null;
  return buildPolygonFiller({
    id: `${run.id}_outside_bend_${cornerIndex}_filler`,
    polygon: plan.wedge,
    floorY,
    height,
    runId: run.id,
    cornerIndex,
  });
}

module.exports = {
  frontPoint,
  isOutsideBend,
  planOutsideBend,
  buildOutsideWedgeBody,
};
